import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import AuthLayout from '@/app/layouts/AuthLayout';
import SignInPage from '@/app/pages/auth/SignInPage';
import SignUpPage from '@/app/pages/auth/SignUpPage';
import { OAuthProfileForm, useOAuthFlow  } from '@auth';
import { errorMessagesOr } from '@/shared/utils/fieldErrors';

export type AuthMode = 'signin' | 'signup';

interface AuthPageProps {
  mode: AuthMode;
  onSwitchMode: (nextMode: AuthMode) => void;
}

export default function AuthPage({ mode, onSwitchMode }: AuthPageProps) {
  const intl = useIntl();
  const oauth = useOAuthFlow();

  const profileErrors = useMemo(
    () => errorMessagesOr(oauth.completeError, intl.formatMessage({ id: 'auth.signUp.failed' })),
    [oauth.completeError, intl]
  );

  const oauthError = oauth.popupErrorMessageId ? intl.formatMessage({ id: oauth.popupErrorMessageId }) : null;

  const switchMode = (nextMode: AuthMode) => {
    oauth.dismissPopupError();
    oauth.clear();
    onSwitchMode(nextMode);
  };

  if (oauth.identity) {
    return (
      <AuthLayout>
        <OAuthProfileForm
          oauthIdentity={oauth.identity}
          serverErrors={profileErrors}
          isLoading={oauth.isCompleting}
          onSubmit={oauth.complete}
          onClearOAuth={oauth.clear}
          onSwitchToSignIn={() => switchMode('signin')}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {mode === 'signup' ? (
        <SignUpPage onSwitchToSignIn={() => switchMode('signin')} onOAuth={oauth.start} oauthError={oauthError} />
      ) : (
        <SignInPage onSwitchToSignUp={() => switchMode('signup')} onOAuth={oauth.start} oauthError={oauthError} />
      )}
    </AuthLayout>
  );
}
